import { isSameDay } from "date-fns";
import {
  Trip,
  Location,
  ItineraryItem,
  ViatorProductResponse,
  ViatorProductInitial,
  TripUser,
} from "@/types";
import { calculateStartTime } from "@/lib/experiences";
import logger from "@/utils/logger";

export const addViatorProductToDay = async (
  originalProduct: ViatorProductResponse,
  initialProduct: ViatorProductInitial,
  tripData: Trip,
  selectedLocation: Location,
  selectedDay: string | null,
  user: TripUser
) => {
  if (!originalProduct) {
    console.error("Viator product not found:", originalProduct);
    return;
  }
  // Find existing items for the selected location and day
  const existingItems = tripData.locations
    .find((loc: any) => loc.id === selectedLocation?.id)
    ?.itinerary_items?.find(
      (item: any) => item.date && isSameDay(item.date, selectedDay)
    );

  // Create a new ItineraryItem object
  const product: ItineraryItem = {
    name: originalProduct.name,
    start_time: existingItems
      ? await calculateStartTime(
          existingItems,
          selectedLocation.date.time_zone || "UTC"
        )
      : new Date("09:00").toLocaleString("en-US", { timeZone: "UTC" }),
    duration: originalProduct.duration ?? 2,
    images: originalProduct.images.slice(0, 2).map((img) => img.highRes),
    coordinates: originalProduct.location[0]?.coordinates || {
      latitude: selectedLocation?.coordinates?.latitude ?? 0,
      longitude: selectedLocation?.coordinates?.longitude ?? 0,
    },
    metadata: {
      description: originalProduct.metadata.description,
      tip: originalProduct.metadata.tip,
      rating: originalProduct.rating,
      num_reviews: originalProduct.total_reviews,
    },
    pricing: initialProduct.from_price,
    id: originalProduct.id + new Date().getTime().toString(),
    sourceId: {
      viatorProductCode: originalProduct.id,
    },
    type: "viator",
    bookingStatus: "pending",
    categories: ["viator-experience"],
    participants: [
      {
        name: user.first_name + " " + user.last_name,
        profile_image: user.profile_image,
        id: user.user_id,
        status: "confirmed",
      },
    ],
  };

  // Add Viator product to selected day
  const updatedLocations = tripData.locations.map((location: Location) => {
    if (location.id === selectedLocation?.id) {
      const itineraryItems = location.itinerary_items || [];

      // Check if the itinerary item for the selected day exists
      const dayItemIndex = itineraryItems.findIndex(
        (item: any) => item.date && isSameDay(item.date, selectedDay)
      );

      if (dayItemIndex !== -1) {
        const dayItem = itineraryItems[dayItemIndex];
        if (dayItem) {
          itineraryItems[dayItemIndex] = {
            ...dayItem,
            items: [...dayItem.items, product],
          };
        }
      } else {
        logger.log("Day Item Index is -1");
        itineraryItems.push({
          date: selectedDay || "",
          items: [product],
        });
      }

      return {
        ...location,
        itinerary_items: itineraryItems,
      };
    } else {
      console.error("Location not found");
    }
    return location;
  });

  // Return the updated trip data
  return {
    tripData: {
      ...tripData,
      locations: updatedLocations,
    },
    updatedLocation: updatedLocations,
  };
};
